import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, addData, updateData, deleteData } from '../../../services/admin/defaultSetup/esSubjectTypeServices';
import { Modal, Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';
import Swal from 'sweetalert2';

const EsSubjectType = () => {
const [dataList, setDataList] = useState([]);
const [form, setForm] = useState({ id: '', type: ''});
const [modalType, setModalType] = useState('');
const [showModal, setShowModal] = useState(false);
const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
const [selectedData, setSelectedData] = useState(null);
const [searchText, setSearchText] = useState('');
const [currentPage, setCurrentPage] = useState(1);
const [loading, setLoading] = useState(true);
const [errors, setErrors] = useState({});

// Fetch data list
const fetchData = useCallback(async () => {
    try {
    const data = await getData();
    setDataList(data);
    } catch (error) {
    console.error('Failed to fetch data:', error);
    }
}, []);

useEffect(() => {
    const fetchDataAsync = async () => {
        setLoading(true); 
        await fetchData();
        // Optional 2 second delay
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setLoading(false); 
      };
    
      fetchDataAsync();
}, [fetchData]);

const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    // If the field is `type`, convert it to lowercase
    if (name === 'type') {
        formattedValue = value.toLowerCase();
    }
    setForm((prev) => ({ ...prev, [name]: formattedValue }));
    // Clear any previous errors for this field
    setErrors((prev) => ({ ...prev, [name]: '' }));
};

const validateForm = () => {
    const newErrors = {};
    // Check if code or name fields are empty or just whitespace
    if (!form.type || !form.type.trim()) {newErrors.type = 'Type is required';}
    // Set the error state if there are validation errors
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;  // Return true if no errors
  };

const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
    if (modalType === 'add') {
        await addData(form);
    } else if (modalType === 'edit') {
        await updateData(form);
    }
    fetchData();
    setShowModal(false);
    // ✅ Show success alert with auto-close
    Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Subject ${modalType === 'add' ? 'added' : 'updated'} successfully!`,
        timer: 2000, // 2 seconds
        showConfirmButton: false,
    });
    } catch (error) {
    console.error('Error during form submission:', error);
    Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Something went wrong while submitting the form!',
    });
    }
};

const handleDelete = async () => {
    if (selectedData) {
    // Optimistically remove the data from the list
    setDataList((prev) => prev.filter((data) => data.id !== selectedData.id));
    // Proceed with the actual delete request
    await deleteData(selectedData);
    setShowDeleteConfirmModal(false);
    }
};

const openModal = (type, data = { id: '', type: '' }) => {
    setErrors({}); // ✅ Clear previous errors
    setModalType(type);
    setForm(data);
    setShowModal(true);
};

const openDeleteConfirmModal = (data) => {
    setSelectedData(data);
    setShowDeleteConfirmModal(true);
};

const handleSearchChange = (e) => {
    setSearchText(e.target.value);
};

const handlePageChange = (page) => {
    setCurrentPage(page);
};

// Memoized filtered data based on search query
const filteredData = useMemo(() => {
    return dataList.filter(
    (data) =>
        data.type.toLowerCase().includes(searchText.toLowerCase())
    )
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)); // Descending by updatedAt
}, [dataList, searchText]);

const columns = [
    {
    name: '#',
    selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
    sortable: false,
    width: '80px',
    },
    {
    name: 'Subject Type',
    selector: (row) => row.type,
    sortable: true,
    },
    {
    name: 'Updated At',
    selector: (row) => row.updatedAt,
    sortable: true,
    },
    {
    name: 'Actions',
    cell: (row) => (
        <div>
        <Button className="me-2" variant="warning" size="sm" onClick={() => openModal('edit', row)} > Edit </Button>
        <Button variant="danger" size="sm" onClick={() => openDeleteConfirmModal(row)} > Delete </Button>
        </div>
    ),
    },
];

return (
    <div className="d-flex">
    <Sidebar />
    <div className="main-content w-100">
        <Header />
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center w-100"
            style={{ minHeight: "300px" }}
          >
            <Spinner/>
          </div>
                  ) :(
        <div>
        <div className="container-fluid p-4">
        <div className="row">
            <div className="col-8">
            <p className='fs-4'>General Configuration / Default Setup / Subject Type Setup</p>
            </div>
            <div className="col-4 text-end">
            <button className="btn btn-primary mb-3" onClick={() => openModal('add')}>
                Add
            </button>
            </div>
        </div>

        <DataTable
            columns={columns}
            data={filteredData}
            pagination
            search
            highlightOnHover
            responsive
            striped
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
            onChangePage={handlePageChange}
            subHeader
            subHeaderComponent={
            <div>
                <input className="form-control" type="text" placeholder="Search" value={searchText}
                onChange={handleSearchChange}
                />
            </div>
            }
        />

        {/* Modal for Add/Edit data */}
        <Modal show={showModal} backdrop="static" onHide={() => setShowModal(false)}>
            <Modal.Header closeButton>
            <Modal.Title>{modalType === 'add' ? 'Add' : 'Edit'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
            <Form>
                <Form.Group className="mb-3">
                <Form.Label>Subject Type</Form.Label>
                <Form.Control type="text" name="type" value={form.type} onChange={handleChange} isInvalid={!!errors.type}/>
                <Form.Control.Feedback type="invalid">  {errors.type} </Form.Control.Feedback>
                </Form.Group>
            </Form>
            </Modal.Body>
            <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}> Cancel </Button>
            <Button variant="primary" onClick={handleSubmit}> {modalType === 'add' ? 'Add' : 'Save'} </Button>
            </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteConfirmModal} onHide={() => setShowDeleteConfirmModal(false)}>
            <Modal.Header closeButton>
            <Modal.Title>Delete</Modal.Title>
            </Modal.Header>
            <Modal.Body>
            <p>Are you sure you want to delete this row?</p>
            </Modal.Body>
            <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteConfirmModal(false)}>
                Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
                Yes, Delete
            </Button>
            </Modal.Footer>
        </Modal>
        </div>
        <Footer />
        </div>
        )};
    </div>
    </div>
);
};

export default EsSubjectType;